import type { User, UserManager } from 'oidc-client-ts';
import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import type { SignInState } from './session.js';

interface Auth {
  /** `undefined` while the stored session is being read; `null` when signed out. */
  user: User | null | undefined;
  manager: UserManager;
  signIn: (returnTo?: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<Auth | null>(null);

export function AuthProvider({ manager, children }: { manager: UserManager; children: ReactNode }) {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  const navigate = useNavigate();

  useEffect(() => {
    let live = true;
    void manager.getUser().then((stored) => {
      if (live) setUser(stored !== null && !stored.expired ? stored : null);
    });

    const loaded = (next: User) => setUser(next);
    const unloaded = () => setUser(null);
    // The refresh token was refused or the Zitadel session ended: say so, rather than bouncing
    // the person through the sign-in page with no explanation.
    const renewFailed = () => {
      void manager.removeUser().then(() => {
        setUser(null);
        navigate('/signed-out', { replace: true });
      });
    };

    manager.events.addUserLoaded(loaded);
    manager.events.addUserUnloaded(unloaded);
    manager.events.addSilentRenewError(renewFailed);
    return () => {
      live = false;
      manager.events.removeUserLoaded(loaded);
      manager.events.removeUserUnloaded(unloaded);
      manager.events.removeSilentRenewError(renewFailed);
    };
  }, [manager, navigate]);

  const value = useMemo<Auth>(
    () => ({
      user,
      manager,
      signIn: (returnTo = '/') =>
        manager.signinRedirect({ state: { returnTo } satisfies SignInState }),
      signOut: () => manager.signoutRedirect(),
    }),
    [user, manager],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): Auth {
  const value = useContext(AuthContext);
  if (value === null) throw new Error('useAuth outside AuthProvider');
  return value;
}
